import React, { useState, useMemo, useCallback } from 'react';

// =============================================================================
// gridFilters — reusable per-column filtering for MantleDataGrid pages.
//
//   const accessors = { name: (r) => r.name, status: (r) => r.status, ... };
//   const { filters, setFilter, clearAll, filtered, activeIds } =
//     useColumnFilters(rows, accessors);
//
// Wire the grid's header menu (column.hasMenu + onHeaderMenuClick) to open
// <FilterPopover> for the clicked column. A column filter is
// { text, values }: `text` = case-insensitive contains; `values` = Excel-style
// allow-list of exact display values (null = all). Rows must pass every active
// column filter.
// =============================================================================

export function useColumnFilters(rows, accessors) {
  const [filters, setFilters] = useState({}); // { colId: { text: '', values: string[]|null } }

  const setFilter = useCallback((colId, f) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (!f || (!f.text && !f.values)) delete next[colId];
      else next[colId] = f;
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setFilters({}), []);
  const activeIds = useMemo(() => Object.keys(filters), [filters]);

  const filtered = useMemo(() => {
    const entries = Object.entries(filters);
    if (entries.length === 0) return rows;
    return rows.filter((r) => entries.every(([colId, f]) => {
      const acc = accessors[colId];
      if (!acc) return true;
      const v = String(acc(r) ?? '');
      if (f.text && !v.toLowerCase().includes(f.text.toLowerCase())) return false;
      if (f.values && !f.values.includes(v)) return false;
      return true;
    }));
  }, [rows, filters, accessors]);

  return { filters, setFilter, clearAll, filtered, activeIds };
}

// Small anchored popover: contains-text box + Excel-style value checklist.
// NOTE: render with key={colId} so the checklist state never leaks between
// different columns' popovers.
export function FilterPopover({ anchor, options, filter, onApply, onClose }) {
  const [text, setText] = useState(filter?.text || '');
  const [vals, setVals] = useState(() => (filter?.values ? new Set(filter.values) : null)); // null = all

  const toggle = (v) => setVals((prev) => {
    const base = prev === null ? new Set(options) : new Set(prev);
    if (base.has(v)) base.delete(v); else base.add(v);
    return base.size === options.length ? null : base;
  });

  // Applying an empty checklist would hide every row — block it (like Excel
  // greying out OK when nothing is ticked).
  const nothingTicked = vals !== null && vals.size === 0;

  const apply = () => {
    if (nothingTicked) return;
    const t = text.trim();
    const values = vals === null ? null : [...vals];
    onApply(t || values ? { text: t, values } : null);
    onClose();
  };
  const clear = () => { onApply(null); onClose(); };

  return (
    <>
      <div className="fixed inset-0 z-40" onMouseDown={onClose} />
      <div
        className="fixed z-50 w-60 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl p-2"
        style={{
          top: Math.min(anchor.y, window.innerHeight - 330),
          left: Math.min(anchor.x, window.innerWidth - 260),
        }}
      >
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') apply(); if (e.key === 'Escape') onClose(); }}
          placeholder="Contains…"
          className="w-full mb-2 px-2.5 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-slate-400"
        />
        <div className="flex items-center justify-between px-1 pb-1">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">Values</span>
          <span className="flex items-center gap-2">
            <button type="button" onClick={() => setVals(null)} className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline">
              All
            </button>
            <button type="button" onClick={() => setVals(new Set())} className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline">
              None
            </button>
          </span>
        </div>
        <div className="max-h-48 overflow-auto rounded-md border border-slate-100 dark:border-slate-700">
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 px-2 py-1 text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-blue-600"
                checked={vals === null || vals.has(o)}
                onChange={() => toggle(o)}
              />
              <span className="truncate">{o === '' ? '(blank)' : o}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={clear} className="px-2.5 py-1 text-xs rounded-md border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
            Clear
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={nothingTicked}
            title={nothingTicked ? 'Tick at least one value' : undefined}
            className="px-2.5 py-1 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}
